import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { GridColDef, GridDataSource, GridGetRowsParams } from '@mui/x-data-grid-pro'
import { Box, Link, Stack, Tab, Typography } from '@mui/material'
import {
  CampaignSnsRequirementSnsType,
  OrderDirection,
  PerformanceMetricsOrderField,
  useCampaignPerformanceMetricsQuery,
  useCampaignPerformanceMetricsSummaryQuery,
  usePostPerformanceMetricsQuery,
  usePostPerformanceMetricsSummaryQuery,
} from '~/generated/graphql'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import React, { SetStateAction, useMemo, useRef, useState } from 'react'
import { TabPanel, TabContext, TabList } from '@mui/lab'
import { DataGridPro } from '~/components/common/DataGrid'
import { ErrorAlert } from '~/components/common/ErrorAlert'
import { SnsIcon } from '~/components/common/SnsIcon'
import { getRandomPlaceholder } from '~/lib/feature/fillCampaignThumbnailWithPlaceholder'
import { pagesPath } from '~/lib/$path'
import { decodeId, encodeId } from '~/lib/convertId'
import { Campaign_SNS_LABEL } from '~/constants/snstype'
import { DateTime } from 'luxon'

import { validateReportDates } from '~/lib/feature/report'
import { logger } from '~/lib/logger'

type DateRangeProps = {
  startDate: string
  endDate: string
}

type TabPanelProps = DateRangeProps & {
  onPaginationChange: () => void
}

const TabValue = {
  Campaign: 'CAMPAIGN',
  Post: 'POST',
} as const

type TabValueType = (typeof TabValue)[keyof typeof TabValue]

type Props = DateRangeProps & {
  isSingleCampaign?: boolean
}

const paginationSettings = {
  pageSize: 5,
  page: 0,
} as const

export const MetricDataGridProWithTabPanel: React.FC<Props> = ({ startDate, endDate, isSingleCampaign }) => {
  const initialTabValue = isSingleCampaign ? TabValue.Post : TabValue.Campaign
  const [value, setValue] = useState<TabValueType>(initialTabValue)
  const boxRef = useRef<HTMLDivElement | null>(null)

  const handleValueChange = (_: React.SyntheticEvent, newValue: SetStateAction<TabValueType>) => {
    setValue(newValue)
  }

  const scrollToTabContext = () => {
    if (boxRef.current) {
      setTimeout(() => {
        boxRef.current?.scrollIntoView({ behavior: 'auto' })
      }, 100)
    }
  }

  return (
    <TabContext value={value}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} ref={boxRef}>
        <TabList onChange={handleValueChange}>
          {!isSingleCampaign && <Tab label="キャンペーン" value={TabValue.Campaign} />}
          <Tab label="投稿" value={TabValue.Post} />
        </TabList>
      </Box>
      <CampaignTabPanel startDate={startDate} endDate={endDate} onPaginationChange={scrollToTabContext} />
      <PostTabPanel
        startDate={startDate}
        endDate={endDate}
        isSingleCampaign={isSingleCampaign}
        onPaginationChange={scrollToTabContext}
      />
    </TabContext>
  )
}

const CampaignTabPanel: React.FC<TabPanelProps> = ({ startDate, endDate, onPaginationChange }) => {
  const formattedStartDate = DateTime.fromISO(startDate)
  const formattedEndDate = DateTime.fromISO(endDate)

  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
  } = useCampaignPerformanceMetricsSummaryQuery({
    variables: {
      input: {
        startedAt: startDate,
        endedAt: endDate,
      },
    },
    skip: !validateReportDates(formattedStartDate, formattedEndDate).isValid,
  })

  const {
    data,
    loading: metricsLoading,
    error: metricsError,
    refetch,
  } = useCampaignPerformanceMetricsQuery({
    variables: {
      input: {
        startedAt: startDate,
        endedAt: endDate,
      },
      pagination: paginationSettings,
    },
    notifyOnNetworkStatusChange: true,
    skip: !validateReportDates(formattedStartDate, formattedEndDate).isValid,
  })

  const loading = metricsLoading || summaryLoading

  const error = metricsError || summaryError

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (params: GridGetRowsParams) => {
        const { paginationModel, sortModel } = params

        const isInvalidReportDates = !validateReportDates(formattedStartDate, formattedEndDate).isValid
        if (isInvalidReportDates) {
          return {
            rows: [],
            rowCount: 0,
          }
        }

        try {
          const result = await refetch({
            pagination: {
              page: paginationModel?.page ?? 0,
              pageSize: paginationModel?.pageSize ?? 25,
            },
            input: {
              startedAt: startDate,
              endedAt: endDate,
            },
            ...(sortModel.length > 0 && {
              order: {
                direction: sortModel[0]?.sort?.toUpperCase() as OrderDirection,
                field: sortModel[0]?.field.toUpperCase() as PerformanceMetricsOrderField,
              },
            }),
          })

          return {
            rows:
              result.data.campaignPerformanceMetrics.nodes?.map((node, index) => ({
                id: node.campaignId + index,
                campaignName: node.campaign.name,
                thumbnailUrl: node.campaign.thumbnailUrls[0],
                impressions: node.impressions,
                postCounts: node.postCounts,
                reaches: node.reaches,
                engagements: node.engagements,
                engagementRate: node.engagementRate,
                likes: node.likes,
                comments: node.comments,
                saves: node.saves,
                shares: node.shares,
              })) || [],
            rowCount: result.data.campaignPerformanceMetrics.totalCount || 0,
          }
        } catch (error) {
          logger.error(new Error('Failed to fetch campaign performance metrics', { cause: error }))
          return {
            rows: [],
            rowCount: 0,
          }
        }
      },
    }),
    [startDate, endDate, refetch],
  )

  if (error) {
    return (
      <Box pt={1}>
        <ErrorAlert
          title="レポートデータの取得に失敗しました。お手数ですが、ページをリロードしてください。"
          error={error}
        />
      </Box>
    )
  }

  return (
    <TabPanel
      value={TabValue.Campaign}
      sx={{
        px: 0,
      }}
    >
      <DataGridPro
        initialState={{
          pagination: {
            paginationModel: paginationSettings,
            rowCount: data?.campaignPerformanceMetrics.totalCount ?? 0,
          },
        }}
        dataSource={dataSource}
        columns={campaignColumns}
        autoHeight
        rowHeight={ROW_HEIGHT}
        getRowHeight={(params) => {
          return params.id === 'pinned-row' ? 50 : ROW_HEIGHT
        }}
        loading={loading}
        pagination
        paginationMode="server"
        pageSizeOptions={[5, 25, 50, 100]}
        onPaginationModelChange={onPaginationChange}
        pinnedRows={{
          top: [{ id: 'pinned-row', ...summaryData?.campaignPerformanceMetricsSummary }],
        }}
        sx={{
          ...dataGridStyles,
        }}
      />
    </TabPanel>
  )
}

const PostTabPanel: React.FC<TabPanelProps & { isSingleCampaign?: boolean }> = ({
  startDate,
  endDate,
  isSingleCampaign,
  onPaginationChange,
}) => {
  const formattedStartDate = DateTime.fromISO(startDate)
  const formattedEndDate = DateTime.fromISO(endDate)
  const router = useRouter()
  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
  } = usePostPerformanceMetricsSummaryQuery({
    variables: {
      input: {
        startedAt: startDate,
        endedAt: endDate,
        ...(isSingleCampaign && { campaignId: encodeId('Campaign', Number(router.query.campaignId)) }),
      },
    },
    skip: !validateReportDates(formattedStartDate, formattedEndDate).isValid,
  })

  const {
    data,
    loading: metricsLoading,
    error: metricsError,
    refetch,
  } = usePostPerformanceMetricsQuery({
    variables: {
      input: {
        startedAt: startDate,
        endedAt: endDate,
        ...(isSingleCampaign && { campaignId: encodeId('Campaign', Number(router.query.campaignId)) }),
      },
      pagination: paginationSettings,
    },
    skip: !validateReportDates(formattedStartDate, formattedEndDate).isValid,
    notifyOnNetworkStatusChange: true,
  })

  const loading = metricsLoading || summaryLoading
  const error = metricsError || summaryError

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (params: GridGetRowsParams) => {
        const { paginationModel, sortModel } = params
        const { campaignId } = router.query

        const isInvalidReportDates = !validateReportDates(formattedStartDate, formattedEndDate).isValid
        if (isInvalidReportDates) {
          return {
            rows: [],
            rowCount: 0,
          }
        }

        try {
          const result = await refetch({
            pagination: {
              page: paginationModel?.page ?? 0,
              pageSize: paginationModel?.pageSize ?? 25,
            },
            input: {
              startedAt: startDate,
              endedAt: endDate,
              ...(isSingleCampaign && { campaignId: encodeId('Campaign', Number(campaignId)) }),
            },
            ...(sortModel.length > 0 && {
              order: {
                direction: sortModel[0]?.sort?.toUpperCase() as OrderDirection,
                field: sortModel[0]?.field.toUpperCase() as PerformanceMetricsOrderField,
              },
            }),
          })

          return {
            rows:
              result.data.postPerformanceMetrics.nodes?.map((node, index) => ({
                id: node.campaignId + index,
                campaignName: node.campaign.name,
                thumbnailUrl: node.campaign.thumbnailUrls[0],
                sns: Campaign_SNS_LABEL[node.snsType],
                impressions: node.impressions,
                reaches: node.reaches,
                engagements: node.engagements,
                engagementRate: node.engagementRate,
                likes: node.likes,
                comments: node.comments,
                saves: node.saves,
                shares: node.shares,
                postId: node.postInfo.postUrl,
                postUrl: node.postInfo.postUrl,
                postThumbnailUrl: node.postInfo.postThumbnailUrl,
                userInfluencerId: node.postInfo.userInfluencerId,
                userInfluencerName: node.postInfo.userInfluencerName,
                snsConnectUid: node.postInfo.snsConnectUid,
                snsConnectName: node.postInfo.snsConnectName,
              })) || [],
            rowCount: result.data.postPerformanceMetrics.totalCount || 0,
            hideFooter: result.data.postPerformanceMetrics.totalCount === 0,
          }
        } catch (err) {
          logger.error(new Error('Failed to fetch post performance metrics', { cause: err }))
          return {
            rows: [],
            rowCount: 0,
          }
        }
      },
    }),
    [startDate, endDate, refetch, isSingleCampaign, router.query],
  )

  if (error) {
    return (
      <ErrorAlert
        title="レポートデータの取得に失敗しました。お手数ですが、ページをリロードしてください。"
        error={error}
      />
    )
  }

  return (
    <TabPanel
      value={TabValue.Post}
      sx={{
        px: 0,
      }}
    >
      <DataGridPro
        initialState={{
          pagination: {
            paginationModel: paginationSettings,
            rowCount: data?.postPerformanceMetrics.totalCount ?? 0,
          },
        }}
        dataSource={dataSource}
        columns={isSingleCampaign ? postColumns : campaignPostColumns}
        autoHeight
        rowHeight={ROW_HEIGHT}
        getRowHeight={(params) => {
          return params.id === 'pinned-row' ? 50 : ROW_HEIGHT
        }}
        loading={loading}
        pagination
        paginationMode="server"
        pageSizeOptions={[5, 25, 50, 100]}
        onPaginationModelChange={onPaginationChange}
        pinnedRows={{
          top: [{ id: 'pinned-row', ...summaryData?.postPerformanceMetricsSummary }],
        }}
        sx={{
          ...dataGridStyles,
        }}
      />
    </TabPanel>
  )
}

const ROW_HEIGHT = 116

const dataGridStyles = {
  '.MuiDataGrid-pinnedRows--top :first-of-type ': {
    backgroundColor: 'grey.200',
  },
}

const renderTextCell = (value: number | null, suffix: string = '') => {
  return <Typography variant="bodyL">{value != null ? `${value.toLocaleString()}${suffix}` : '-'}</Typography>
}

const CampaignCell: React.FC<{ thumbnailUrl?: string; campaignName: string; campaignId: string }> = ({
  thumbnailUrl,
  campaignName,
  campaignId,
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
    >
      <Box
        component="img"
        src={thumbnailUrl ?? getRandomPlaceholder(campaignId)}
        alt={campaignName}
        sx={{ width: 100, aspectRatio: '1', objectFit: 'cover', mr: 1 }}
      />
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        <NextLink href={pagesPath.campaign_summary._campaignId(decodeId(campaignId).id).$url()} passHref legacyBehavior>
          <Link sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body1"
              color="primary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
            >
              {campaignName}
            </Typography>
          </Link>
        </NextLink>
        <Typography variant="body2" color="textSecondary">
          {decodeId(campaignId).id}
        </Typography>
      </Box>
    </Box>
  )
}

const InfluencerCell: React.FC<{ influencerName: string; influencerId: string }> = ({
  influencerName,
  influencerId,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      <Typography
        component="span"
        variant="body1"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {influencerName}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {decodeId(influencerId).id}
      </Typography>
    </Box>
  )
}

const PostLinkCell: React.FC<{ postUrl: string }> = ({ postUrl }) => {
  return (
    <NextLink href={postUrl} passHref legacyBehavior>
      <Link target="_blank" rel="noreferrer noopener">
        閲覧
        <OpenInNewIcon sx={{ fontSize: 16 }} />
      </Link>
    </NextLink>
  )
}

const SnsCell: React.FC<{ snsType: CampaignSnsRequirementSnsType; snsText: string }> = ({ snsType, snsText }) => {
  return (
    <Stack spacing={0.5} direction="row" alignItems="center">
      <SnsIcon snsType={snsType} />
      <Typography variant="bodyL">{snsText}</Typography>
    </Stack>
  )
}

const ThumbnailCell: React.FC<{ thumbnailUrl?: string; altText: string }> = ({ thumbnailUrl, altText }) => {
  return (
    <img
      src={thumbnailUrl ?? '/campaign_placeholder/placeholder_3.jpg'}
      alt={altText}
      style={{ width: 100, aspectRatio: 1, objectFit: 'cover' }}
    />
  )
}

const campaignColumns: GridColDef[] = [
  {
    field: 'campaign',
    headerName: 'キャンペーン',
    flex: 1,
    minWidth: 300,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null // 合計値行では表示しない
      return (
        <CampaignCell
          thumbnailUrl={params.row.thumbnailUrl}
          campaignName={params.row.campaignName}
          campaignId={params.row.id}
        />
      )
    },
  },
  {
    field: 'impression',
    headerName: '表示回数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.impressions),
  },
  {
    field: 'post_count',
    headerName: '投稿数',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.postCounts),
  },
  {
    field: 'reach',
    headerName: 'リーチ数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.reaches),
  },
  {
    field: 'engagement',
    headerName: 'エンゲージメント数',
    width: 180,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.engagements),
  },
  {
    field: 'engagement_rate',
    headerName: 'エンゲージメント率',
    width: 180,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.engagementRate, '%'),
  },
  {
    field: 'like',
    headerName: 'いいね数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.likes),
  },
  {
    field: 'comment',
    headerName: 'コメント数',
    width: 130,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.comments),
  },
  {
    field: 'save',
    headerName: '保存数',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.saves),
  },
  {
    field: 'share',
    headerName: 'シェア数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.shares, '件'),
  },
]

const campaignPostColumns: GridColDef[] = [
  {
    field: 'campaign',
    headerName: 'キャンペーン',
    flex: 1,
    minWidth: 300,
    maxWidth: 400,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return (
        <CampaignCell
          thumbnailUrl={params.row.thumbnailUrl}
          campaignName={params.row.campaignName}
          campaignId={params.row.id}
        />
      )
    },
  },
  {
    field: 'influencer',
    headerName: 'インフルエンサー',
    flex: 1,
    minWidth: 200,
    maxWidth: 250,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return (
        <InfluencerCell influencerName={params.row.userInfluencerName} influencerId={params.row.userInfluencerId} />
      )
    },
  },
  {
    field: 'postImage',
    headerName: '投稿画像',
    width: 150,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return <ThumbnailCell thumbnailUrl={params.row.postThumbnailUrl} altText={params.row.sns} />
    },
  },
  {
    field: 'viewPost',
    headerName: '投稿リンク',
    width: 130,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return <PostLinkCell postUrl={params.row.postUrl} />
    },
  },
  {
    field: 'sns',
    headerName: 'SNS',
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return <SnsCell snsType={params.row.sns.toUpperCase()} snsText={params.row.sns} />
    },
  },
  {
    field: 'impression',
    headerName: '表示回数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.impressions)
    },
  },
  {
    field: 'reach',
    headerName: 'リーチ数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.reaches)
    },
  },
  {
    field: 'engagement',
    headerName: 'エンゲージメント数',
    width: 180,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.engagements)
    },
  },
  {
    field: 'engagement_rate',
    headerName: 'エンゲージメント率',
    width: 180,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.engagementRate, '%')
    },
  },
  {
    field: 'like',
    headerName: 'いいね数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.likes)
    },
  },
  {
    field: 'comment',
    headerName: 'コメント数',
    width: 130,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.comments)
    },
  },
  {
    field: 'save',
    headerName: '保存数',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.saves)
    },
  },
  {
    field: 'share',
    headerName: 'シェア数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => {
      return renderTextCell(params.row.shares, '件')
    },
  },
]

const postColumns: GridColDef[] = [
  {
    field: 'post',
    headerName: '投稿',
    flex: 1,
    minWidth: 300,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return (
        <Box
          display="flex"
          alignItems="center"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <Box
            component="img"
            src={params.row.thumbnailUrl ?? '/campaign_placeholder/placeholder_1.jpg'}
            alt={params.row.campaignName}
            sx={{ width: 100, aspectRatio: '1', objectFit: 'cover', mr: 1 }}
          />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Typography
              component="span"
              variant="body1"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {params.row.campaignName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {decodeId(params.row.id).id}
            </Typography>
          </Box>
        </Box>
      )
    },
  },
  {
    field: 'influencer',
    headerName: 'インフルエンサー',
    flex: 1,
    minWidth: 200,
    maxWidth: 250,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return (
        <InfluencerCell influencerName={params.row.userInfluencerName} influencerId={params.row.userInfluencerId} />
      )
    },
  },
  {
    field: 'postImage',
    headerName: '投稿画像',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return <ThumbnailCell thumbnailUrl={params.row.postThumbnailUrl} altText={params.row.sns} />
    },
  },
  {
    field: 'viewPost',
    headerName: '投稿リンク',
    width: 130,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return <PostLinkCell postUrl={params.row.postUrl} />
    },
  },
  {
    field: 'sns',
    headerName: 'SNS',
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: (params) => {
      if (params.row.id === 'pinned-row') return null
      return <SnsCell snsType={params.row.sns.toUpperCase()} snsText={params.row.sns} />
    },
  },
  {
    field: 'impression',
    headerName: '表示回数',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.impressions),
  },
  {
    field: 'reach',
    headerName: 'リーチ数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.reaches),
  },
  {
    field: 'engagement',
    headerName: 'エンゲージメント数',
    width: 180,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.engagements),
  },
  {
    field: 'engagement_rate',
    headerName: 'エンゲージメント率',
    width: 180,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.engagementRate, '%'),
  },
  {
    field: 'like',
    headerName: 'いいね数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.likes),
  },
  {
    field: 'comment',
    headerName: 'コメント数',
    width: 140,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.comments),
  },
  {
    field: 'save',
    headerName: '保存数',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.saves),
  },
  {
    field: 'share',
    headerName: 'シェア数',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => renderTextCell(params.row.shares, '件'),
  },
]
